import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckIcon, FileTextIcon, SettingsIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon, EuroIcon, LayersIcon, ShieldCheckIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function KostenWebsiteAccountantPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Kosten website accountant | Prijzen 2026 | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Wat kost een website voor een accountant? Prijzen vanaf €1.500 voor een professionele accountantswebsite. Inclusief klantenportaal, SEO en meer.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Kosten website accountant | Prijzen 2026 | Noord',
      'og:description': 'Wat kost een website voor een accountant? Prijzen vanaf €1.500 voor een professionele accountantswebsite.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/cluster/kosten-website-accountant',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const prijsfactoren = [{
    icon: FileTextIcon,
    title: "Aantal pagina's",
    description: "Een eenvoudige website met een homepagina, dienstenpagina en contactpagina is goedkoper dan een uitgebreide site met aparte pagina's voor elke dienst, een blog en een kennisbank."
  }, {
    icon: SettingsIcon,
    title: 'Functionaliteiten',
    description: 'Heeft jouw accountantskantoor een klantenportaal, afsprakenplanner of koppeling met boekhoudpakketten nodig? Elke extra functionaliteit beïnvloedt de prijs.'
  }, {
    icon: SearchIcon,
    title: 'SEO',
    description: 'Basis SEO is standaard inbegrepen. Wil je gericht gevonden worden op zoektermen als "accountant Alkmaar" of "boekhouder in de buurt"? Dan is uitgebreide SEO een slimme investering.'
  }];
  const kostenVoorbeelden = [{
    titel: 'Starter website',
    prijs: '€500 – €1.200',
    beschrijving: 'Een compacte website voor een startende accountant of eenmanszaak.',
    kenmerken: ['Homepagina + dienstenpagina + contact', 'Responsive design', 'Contactformulier', 'Basis SEO-optimalisatie']
  }, {
    titel: 'Professionele bedrijfswebsite',
    prijs: '€1.500 – €3.500',
    beschrijving: 'De meest gekozen optie voor gevestigde accountantskantoren.',
    kenmerken: ["5-10 pagina's met alle diensten", 'WordPress CMS voor zelf beheren', 'SEO-optimalisatie voor lokale vindbaarheid', 'Blog voor kennisdeling', 'Google Analytics integratie'],
    populair: true
  }, {
    titel: 'Maatwerk website',
    prijs: '€3.000 – €6.000+',
    beschrijving: 'Voor grotere kantoren met specifieke wensen en integraties.',
    kenmerken: ["Onbeperkt pagina's", 'Klantenportaal of afsprakenplanner', 'Koppeling met boekhoudpakketten', 'Geavanceerde SEO-strategie', 'Doorlopend onderhoud en support']
  }];
  const faqs = [{
    q: 'Wat kost een website voor een accountant gemiddeld?',
    a: "Een professionele website voor een accountantskantoor kost gemiddeld tussen de €1.500 en €3.500. Dit is afhankelijk van het aantal pagina's, de gewenste functionaliteiten en de mate van SEO-optimalisatie. Een eenvoudige starterswebsite kan al vanaf €500."
  }, {
    q: 'Zijn er maandelijkse kosten na oplevering?',
    a: 'Ja, naast de eenmalige bouwkosten zijn er maandelijkse kosten voor hosting en onderhoud. Bij Noord Digital bieden wij transparante onderhoudspakketten zodat jouw website altijd snel, veilig en up-to-date blijft.'
  }, {
    q: 'Kan ik zelf de website aanpassen?',
    a: 'Ja, wij bouwen op WordPress waardoor je eenvoudig zelf teksten, afbeeldingen en blogartikelen kunt aanpassen. Na oplevering geven wij een korte instructie zodat je direct aan de slag kunt.'
  }, {
    q: 'Is SEO inbegrepen bij de website?',
    a: 'Basis SEO is altijd inbegrepen: snelle laadtijden, juiste meta-tags en een goede sitestructuur. Wil je gericht gevonden worden op zoektermen als "accountant Alkmaar"? Dan adviseren wij een uitgebreid SEO-pakket.'
  }, {
    q: 'Hoe lang duurt het om een website voor een accountant te bouwen?',
    a: 'Een standaard bedrijfswebsite voor een accountantskantoor is gemiddeld binnen 2 tot 4 weken live. Dit hangt af van de omvang en hoe snel wij de benodigde content ontvangen.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1272" data-mp="23c2n1pe0xfon">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1273" data-mp="23dvpstlinfrd">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1274" data-mp="28c72rd73y6dt">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1275" data-mp="3err6i6s2jwkp" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1276" data-mp="3jopxiggfrneo" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1277" data-mp="1jpw6i6qape2f" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1278" data-mp="1one8te9t4v1g">
          <div className="max-w-3xl" data-id="element-1279" data-mp="10yd33ysczxzv">
            <div className="flex items-center gap-2 mb-4 animate-fade-in-up" data-id="element-1280" data-mp="10v7gcq77d87b">
              <a href="#/sector/zakelijke-dienstverlening" className="text-accent-light hover:text-white transition-colors font-semibold text-sm tracking-widest uppercase" data-id="element-1281" data-mp="vzs4u4wo3oe2">
                Zakelijke Dienstverlening
              </a>
              <span className="text-navy-400 text-sm" data-id="element-1282" data-mp="h89fvhltyjik">/</span>
              <a href="#/sector/zakelijke-dienstverlening/accountant" className="text-accent-light hover:text-white transition-colors font-semibold text-sm tracking-widest uppercase" data-id="element-1283" data-mp="d7plglsn5u04">
                Accountant
              </a>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-1284" data-mp="3pb0ntx0vkhzi">
              Wat kost een website voor een accountant?
            </h1>
            <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mb-8 animate-fade-in-up-delay-2" data-id="element-1285" data-mp="3u7qzm85ncrf4">
              Een overzicht van prijzen en opties voor een professionele website
              voor accountants en accountantskantoren.
            </p>
            <div className="animate-fade-in-up-delay-3" data-id="element-1286" data-mp="bvb0lb6gp0z7">
              <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1287" data-mp="guxezny10n9l">
                Vraag een prijsindicatie aan
                <ArrowRightIcon className="w-5 h-5" data-id="element-1288" data-mp="2iw6tfs0q5f3i" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Gemiddelde kosten */}
      <section className="py-24 bg-surface-muted" data-id="element-1289" data-mp="28vxui6fclfkf">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1290" data-mp="28ysg41a3l6ff">
          <div className="text-center mb-16" data-id="element-1291" data-mp="24wfik05i8etq">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1292" data-mp="31ldevmswkkt0">
              Gemiddelde kosten voor een website voor accountants
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1293" data-mp="2wmtrrj2kp2z2">
              De prijs van een website voor een accountantskantoor hangt af van
              de omvang en functionaliteiten. Hieronder een overzicht.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1294" data-mp="3kbmjf3c5wonv">
            {kostenVoorbeelden.map(voorbeeld => <div key={voorbeeld.titel} className={`bg-white rounded-card shadow-card p-8 flex flex-col relative ${voorbeeld.populair ? 'border-2 border-accent md:-translate-y-2' : ''}`} data-id="element-1295" data-mp="3fk3dj6vdesjh">
                {voorbeeld.populair && <div className="absolute -top-3 right-6 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-1296" data-mp="1b2ora4zbayxm">
                    Meest gekozen
                  </div>}
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1297" data-mp="1l1w32nqeelim">
                  {voorbeeld.titel}
                </h3>
                <div className="text-3xl font-extrabold text-accent mb-4" data-id="element-1298" data-mp="1pzfj9t6np8d7">
                  {voorbeeld.prijs}
                </div>
                <p className="text-slate-600 mb-6 flex-grow" data-id="element-1299" data-mp="1uta8fc516fjo">
                  {voorbeeld.beschrijving}
                </p>
                <ul className="space-y-3" data-id="element-1300" data-mp="23rvkqpjwsyc0">
                  {voorbeeld.kenmerken.map(kenmerk => <li key={kenmerk} className="flex items-start gap-3" data-id="element-1301" data-mp="28qp2iu68bbhz">
                      <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1302" data-mp="28oucx2k0iv7k" />
                      <span className="text-slate-700 text-sm" data-id="element-1303" data-mp="1gbhtc82ac9wr">{kenmerk}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      {/* Wat bepaalt de prijs */}
      <section className="py-24 bg-white" data-id="element-1304" data-mp="1u1bjctpj1082">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1305" data-mp="1u2cx55xxg0th">
          <div className="text-center mb-16" data-id="element-1306" data-mp="1yz3avmm9ekbq">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1307" data-mp="1z2iritvl01f9">
              Wat bepaalt de prijs van een website voor een accountant?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1308" data-mp="28zncwrhlrsv0">
              Drie factoren die de kosten van jouw accountantswebsite
              beïnvloeden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1309" data-mp="elmfmb1pbdx1">
            {prijsfactoren.map(factor => <div key={factor.title} className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-1310" data-mp="im6g9olhgznx">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1311" data-mp="6bf3z1re8qb9">
                  <factor.icon className="w-6 h-6 text-accent" data-id="element-1312" data-mp="1dlyyc1tu2ck" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1313" data-mp="3tgwjucaciqde">
                  {factor.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1314" data-mp="tzydfkm6j94x">
                  {factor.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Kosten voorbeelden — uitgebreid */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-1315" data-mp="l2e6pw5jskmx">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-1316" data-mp="g17225ypc1kh" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1317" data-mp="328lbj81c7w6p">
          <div className="text-center mb-16" data-id="element-1318" data-mp="377xuvit02l4j">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1319" data-mp="376mvdv6l7zj8">
              Waar betaal je voor bij een accountantswebsite?
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-1320" data-mp="2x9i4xj5plhwm">
              Een overzicht van de onderdelen die in de prijs zijn opgenomen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1321" data-mp="2jgagg0cxd2j5">
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1322" data-mp="2oaekaaqw8ug0">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-4" data-id="element-1323" data-mp="29liigvffkdnu">
                <EuroIcon className="w-5 h-5 text-accent-light" data-id="element-1324" data-mp="2ejtnmsus5zzq" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1325" data-mp="20ku0jpmx1t5o">
                Design op maat
              </h3>
              <p className="text-navy-300 text-sm leading-relaxed" data-id="element-1326" data-mp="25i2n02f3kjcq">
                Een professioneel ontwerp dat past bij de uitstraling van jouw
                accountantskantoor en vertrouwen wekt bij potentiële klanten.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1327" data-mp="1uy0ano6kn2x7">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-4" data-id="element-1328" data-mp="1qw55u9pfm7eo">
                <LayersIcon className="w-5 h-5 text-accent-light" data-id="element-1329" data-mp="24v4w1lsleucy" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1330" data-mp="1zwlcfs1h1unr">
                Technische bouw
              </h3>
              <p className="text-navy-300 text-sm leading-relaxed" data-id="element-1331" data-mp="2elzior9r3vyx">
                Een snelle, veilige en responsive website gebouwd in WordPress,
                zodat je zelf eenvoudig content kunt aanpassen.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1332" data-mp="2nmfj1rv76mzk">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-4" data-id="element-1333" data-mp="2ionu96pungzl">
                <SearchIcon className="w-5 h-5 text-accent-light" data-id="element-1334" data-mp="gxdurx5m3dyj" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1335" data-mp="lvp5ea3qdsht">
                SEO-optimalisatie
              </h3>
              <p className="text-navy-300 text-sm leading-relaxed" data-id="element-1336" data-mp="qoi9qwvaeytd">
                Technische SEO, meta-tags en gestructureerde content zodat jouw
                kantoor gevonden wordt op relevante zoektermen.
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6" data-id="element-1337" data-mp="2xmajeoi51c3">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-4" data-id="element-1338" data-mp="6zps8l1rl25t">
                <ShieldCheckIcon className="w-5 h-5 text-accent-light" data-id="element-1339" data-mp="bykjmbdsrz1j" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1340" data-mp="1iewg880z18v0">
                Hosting & onderhoud
              </h3>
              <p className="text-navy-300 text-sm leading-relaxed" data-id="element-1341" data-mp="1ndg3cbrawqoy">
                Betrouwbare hosting, SSL-certificaat, backups en technisch
                onderhoud zodat jouw website altijd online en veilig is.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-1342" data-mp="1sl5w3xuk6bxu">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-semibold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-base group" data-id="element-1343" data-mp="1nr1n791qftu4">
              Vraag een vrijblijvende offerte aan
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" data-id="element-1344" data-mp="14sz3n9ryep3p" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-1345" data-mp="2z79s3h4mzh8y">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1346" data-mp="2z5yqnnynyll0">
          <div className="text-center mb-16" data-id="element-1347" data-mp="2v551kr9hj0qj">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1348" data-mp="2v66gj6tmg7dj">
              Veelgestelde vragen over kosten website accountant
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1349" data-mp="xarf2cq9mr9k">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl overflow-hidden shadow-soft" data-id="element-1350" data-mp="jiuqgint2zhx" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1351" data-mp="nk69u6g36oho">
                  <span className="font-bold text-navy-900 pr-4" data-id="element-1352" data-mp="1g7snk7j139i9">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1353" data-mp="12b5ifhgx9d19" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1354" data-mp="3mz1l5zo8tmek" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1355" data-mp="3rzfrnda74oah">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-1356" data-mp="3tfjtah5gpm89">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1357" data-mp="3og77lzqbul79">
          <h3 className="text-lg font-semibold text-navy-900 text-center mb-8" data-id="element-1358" data-mp="3oh066df4u4eb">
            Gerelateerde pagina's
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-1359" data-mp="2vrjly31o1tt6">
            <a href="#/sector/zakelijke-dienstverlening/accountant" className="flex items-center justify-between bg-surface-muted p-5 rounded-card border border-navy-100 hover:shadow-medium transition-all group" data-id="element-1360" data-mp="2vpowcbfg9dir">
              <span className="font-medium text-navy-900" data-id="element-1361" data-mp="2hz3f0r5n9bmz">
                Website accountant
              </span>
              <ArrowRightIcon className="w-4 h-4 text-accent group-hover:translate-x-1 transition-transform" data-id="element-1362" data-mp="2d0ilfakwdq7i" />
            </a>
            <a href="#/website-laten-maken" className="flex items-center justify-between bg-surface-muted p-5 rounded-card border border-navy-100 hover:shadow-medium transition-all group" data-id="element-1363" data-mp="1rk3brqk5j5hq">
              <span className="font-medium text-navy-900" data-id="element-1364" data-mp="26fiytbmbe14m">
                Website laten maken
              </span>
              <ArrowRightIcon className="w-4 h-4 text-accent group-hover:translate-x-1 transition-transform" data-id="element-1365" data-mp="2afjo5raybwqg" />
            </a>
            <a href="#/kosten-website-laten-maken" className="flex items-center justify-between bg-surface-muted p-5 rounded-card border border-navy-100 hover:shadow-medium transition-all group" data-id="element-1366" data-mp="2fe3c1x93vilg">
              <span className="font-medium text-navy-900" data-id="element-1367" data-mp="2u4ke9itkecje">
                Kosten website laten maken
              </span>
              <ArrowRightIcon className="w-4 h-4 text-accent group-hover:translate-x-1 transition-transform" data-id="element-1368" data-mp="2y5dzu5jjbf94" />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1369" data-mp="igp7tidqyobv" />
    </main>;
}